import React, { useState, useEffect, useRef } from "react";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "http://localhost:5001";
const axiosCfg = { withCredentials: true };

// ---------------------------------------------------------------------------
// HELPERS
// ---------------------------------------------------------------------------

function fmt_time(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return dt; }
}

function fmt_datetime(dt) {
  if (!dt) return "No executions yet";
  try {
    return new Date(dt).toLocaleString();
  } catch { return dt; }
}

function hours_color(h) {
  if (h >= 72) return "#dc2626";
  if (h >= 24) return "#d97706";
  return "#16a34a";
}

function rate_color(r) {
  if (r >= 80) return "#16a34a";
  if (r >= 50) return "#d97706";
  return "#dc2626";
}

function conf_color(c) {
  const pct = c * 100;
  if (pct >= 97) return "#16a34a";
  if (pct >= 90) return "#d97706";
  return "#dc2626";
}

// ---------------------------------------------------------------------------
// SUB-COMPONENTS
// ---------------------------------------------------------------------------

function Pill({ label, color, bg }) {
  return (
    <span style={{
      background: bg || "#e0f2fe",
      color: color || "#0369a1",
      borderRadius: 12,
      padding: "2px 10px",
      fontSize: 12,
      fontWeight: 600,
    }}>
      {label}
    </span>
  );
}

function MetricBox({ label, value, valueColor }) {
  return (
    <div style={{ flex: 1, padding: "12px 16px", textAlign: "center" }}>
      <div style={{ fontSize: 13, color: "#64748b", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: valueColor || "#1e293b" }}>{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LOGIN PAGE
// ---------------------------------------------------------------------------

function LoginPage({ onLogin }) {
  const [activeTab, setActiveTab] = useState("company");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      let res;
      if (activeTab === "company") {
        res = await axios.post(`${API}/login`, { company_password: password, remember }, axiosCfg);
      } else {
        res = await axios.post(`${API}/admin/login`, { password, remember }, axiosCfg);
      }
      onLogin(res.data);
    } catch {
      setError("Invalid password");
    } finally {
      setSubmitting(false);
    }
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    setPassword("");
    setError(null);
  };

  return (
    <div style={{
      minHeight: "100vh", background: "#0D1B2A",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
    }}>
      <div style={{
        background: "#fff", borderRadius: 12, padding: "40px 36px",
        width: 380, boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
      }}>
        <div style={{
          color: "#1A9B8C", fontWeight: 800, fontSize: 28,
          textAlign: "center", marginBottom: 28, letterSpacing: 1,
        }}>
          FLONANCE
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", marginBottom: 24, borderBottom: "1px solid #e2e8f0" }}>
          {[
            { id: "company", label: "Company Login" },
            { id: "admin", label: "Admin Login" },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => switchTab(tab.id)}
              style={{
                flex: 1, background: "none", border: "none", cursor: "pointer",
                padding: "10px 0", fontSize: 14, fontWeight: 600,
                color: activeTab === tab.id ? "#1A9B8C" : "#94a3b8",
                borderBottom: activeTab === tab.id ? "2px solid #1A9B8C" : "2px solid transparent",
                marginBottom: -1,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          <label style={{ fontSize: 13, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>
            {activeTab === "company" ? "Company Password" : "Admin Password"}
          </label>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            style={{
              width: "100%", padding: "10px 12px", borderRadius: 6,
              border: "1px solid #cbd5e1", fontSize: 14, boxSizing: "border-box",
              marginBottom: error ? 4 : 16,
            }}
            autoFocus
          />
          {error && (
            <div style={{ color: "#dc2626", fontSize: 13, marginBottom: 12 }}>{error}</div>
          )}

          <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={remember}
              onChange={e => setRemember(e.target.checked)}
              style={{ accentColor: "#1A9B8C" }}
            />
            <span style={{ fontSize: 13, color: "#64748b" }}>Keep me signed in</span>
          </label>

          <button
            type="submit"
            disabled={submitting || !password}
            style={{
              width: "100%", padding: "12px 0", borderRadius: 6,
              background: submitting || !password ? "#94a3b8" : "#1A9B8C",
              color: "#fff", border: "none", fontSize: 15, fontWeight: 700,
              cursor: submitting || !password ? "default" : "pointer",
            }}
          >
            {submitting ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PORTFOLIO VIEW
// ---------------------------------------------------------------------------

function PortfolioView({ data, loading }) {
  if (loading || !data) return <p style={{ color: "#64748b" }}>Loading...</p>;

  const alertBar = {
    green:  { bg: "#0d9488", text: "All systems healthy" },
    yellow: { bg: "#d97706", text: "Attention needed" },
    red:    { bg: "#dc2626", text: "Action required — intervention needed" },
  };

  const statusPill = {
    Live:          { bg: "#dcfce7", color: "#15803d" },
    "Shadow Mode": { bg: "#dbeafe", color: "#1d4ed8" },
    Paused:        { bg: "#e2e8f0", color: "#475569" },
  };

  return (
    <div>
      {data.map((p, i) => {
        const bar = alertBar[p.alert_status] || alertBar.yellow;
        const pill = statusPill[p.status] || statusPill["Shadow Mode"];
        return (
          <div key={i} style={{
            background: "#fff",
            borderRadius: 10,
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            marginBottom: 20,
            overflow: "hidden",
          }}>
            {/* Card header */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "16px 20px", borderBottom: "1px solid #f1f5f9",
            }}>
              <span style={{ fontSize: 18, fontWeight: 700, color: "#1e293b" }}>{p.portco_name}</span>
              <Pill label={p.status} bg={pill.bg} color={pill.color} />
            </div>

            {/* Metrics row */}
            <div style={{ display: "flex", borderBottom: "1px solid #f1f5f9" }}>
              <MetricBox
                label="Automation Rate"
                value={`${p.automation_rate}%`}
                valueColor={rate_color(p.automation_rate)}
              />
              <div style={{ width: 1, background: "#f1f5f9" }} />
              <MetricBox
                label="Open Triage"
                value={`${p.open_triage_count} items`}
                valueColor={p.open_triage_count > 0 ? "#dc2626" : "#16a34a"}
              />
              <div style={{ width: 1, background: "#f1f5f9" }} />
              <MetricBox
                label="Oldest Open"
                value={`${p.oldest_open_hours}h`}
                valueColor={hours_color(p.oldest_open_hours)}
              />
              <div style={{ width: 1, background: "#f1f5f9" }} />
              <MetricBox
                label="Last Execution"
                value={fmt_datetime(p.last_execution)}
                valueColor="#1e293b"
              />
            </div>

            {/* Alert bar */}
            <div style={{
              background: bar.bg, color: "#fff",
              padding: "8px 20px", fontSize: 13, fontWeight: 600,
            }}>
              {bar.text}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TRIAGE VIEW
// ---------------------------------------------------------------------------

function TriageView({ data }) {
  const [expanded, setExpanded] = useState(null);

  if (!data.length) return (
    <p style={{ color: "#16a34a", fontWeight: 600 }}>No open triage items. All clear.</p>
  );

  return (
    <div>
      <p style={{ color: "#64748b", marginBottom: 12 }}>
        <strong style={{ color: "#1e293b" }}>{data.length}</strong> open triage item{data.length !== 1 ? "s" : ""}
      </p>
      <div style={{ background: "#fff", borderRadius: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#f8fafc", color: "#64748b", textAlign: "left" }}>
              {["Vendor", "Invoice #", "Amount", "Code", "Hours Open", "Nudged", "Alerted", "Status"].map(h => (
                <th key={h} style={{ padding: "10px 14px", fontWeight: 600, borderBottom: "1px solid #e2e8f0" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((r, i) => (
              <React.Fragment key={r.record_id}>
                <tr
                  onClick={() => setExpanded(expanded === r.record_id ? null : r.record_id)}
                  style={{
                    background: i % 2 === 0 ? "#fff" : "#f8fafc",
                    cursor: "pointer",
                  }}
                >
                  <td style={{ padding: "10px 14px", borderBottom: "1px solid #e2e8f0" }}>{r.vendor_name}</td>
                  <td style={{ padding: "10px 14px", borderBottom: "1px solid #e2e8f0" }}>{r.invoice_number}</td>
                  <td style={{ padding: "10px 14px", borderBottom: "1px solid #e2e8f0" }}>
                    {r.total_amount != null ? `$${Number(r.total_amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : "—"}
                  </td>
                  <td style={{ padding: "10px 14px", borderBottom: "1px solid #e2e8f0" }}>
                    <span style={{ background: "#fee2e2", color: "#b91c1c", borderRadius: 8, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
                      {r.triage_code}
                    </span>
                  </td>
                  <td style={{ padding: "10px 14px", borderBottom: "1px solid #e2e8f0", fontWeight: 700, color: hours_color(r.hours_open) }}>
                    {r.hours_open}h
                  </td>
                  <td style={{ padding: "10px 14px", borderBottom: "1px solid #e2e8f0", color: r.nudge_sent ? "#16a34a" : "#94a3b8" }}>
                    {r.nudge_sent ? "✓" : "—"}
                  </td>
                  <td style={{ padding: "10px 14px", borderBottom: "1px solid #e2e8f0", color: r.alerted ? "#dc2626" : "#94a3b8" }}>
                    {r.alerted ? "✓" : "—"}
                  </td>
                  <td style={{ padding: "10px 14px", borderBottom: "1px solid #e2e8f0" }}>{r.status}</td>
                </tr>
                {expanded === r.record_id && (
                  <tr style={{ background: "#fef9c3" }}>
                    <td colSpan={8} style={{ padding: "12px 14px", borderBottom: "1px solid #e2e8f0", fontSize: 13, color: "#1e293b" }}>
                      <strong>Message:</strong> {r.triage_message_full}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AUDIT LOG VIEW
// ---------------------------------------------------------------------------

function AuditView({ data }) {
  const [search, setSearch] = useState("");
  const filtered = data.filter(r =>
    !search || (r.vendor_name || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <input
        placeholder="Filter by vendor name..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{
          width: "100%", maxWidth: 320, padding: "8px 12px", borderRadius: 6,
          border: "1px solid #cbd5e1", fontSize: 13, marginBottom: 14, boxSizing: "border-box",
        }}
      />
      <div style={{ background: "#fff", borderRadius: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#f8fafc", color: "#64748b", textAlign: "left" }}>
              {["Job ID", "Vendor", "Invoice #", "Amount", "GL Code", "Confidence", "Timestamp", "Bill ID"].map(h => (
                <th key={h} style={{ padding: "10px 14px", fontWeight: 600, borderBottom: "1px solid #e2e8f0" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => (
              <tr key={r.record_id || i} style={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
                <td style={{ padding: "10px 14px", borderBottom: "1px solid #e2e8f0", fontFamily: "monospace", fontSize: 11 }}>
                  {(r.job_id || "").substring(0, 16)}…
                </td>
                <td style={{ padding: "10px 14px", borderBottom: "1px solid #e2e8f0" }}>{r.vendor_name}</td>
                <td style={{ padding: "10px 14px", borderBottom: "1px solid #e2e8f0" }}>{r.invoice_number}</td>
                <td style={{ padding: "10px 14px", borderBottom: "1px solid #e2e8f0" }}>{r.total_amount_formatted}</td>
                <td style={{ padding: "10px 14px", borderBottom: "1px solid #e2e8f0" }}>{r.assigned_gl_code}</td>
                <td style={{ padding: "10px 14px", borderBottom: "1px solid #e2e8f0", fontWeight: 700, color: conf_color(r.composite_confidence || 0) }}>
                  {r.composite_confidence != null ? `${(r.composite_confidence * 100).toFixed(1)}%` : "—"}
                </td>
                <td style={{ padding: "10px 14px", borderBottom: "1px solid #e2e8f0", color: "#64748b" }}>
                  {fmt_datetime(r.executor_completed_at)}
                </td>
                <td style={{ padding: "10px 14px", borderBottom: "1px solid #e2e8f0", fontFamily: "monospace", fontSize: 11 }}>
                  {r.erp_bill_id || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{ color: "#94a3b8", fontSize: 12, marginTop: 8 }}>
        Showing {filtered.length} of {data.length} entries
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TRUTH BASE VIEW
// ---------------------------------------------------------------------------

function TruthBaseView({ data }) {
  if (!data) return <p style={{ color: "#64748b" }}>Loading...</p>;

  const metrics = [
    {
      label: "Vendors",
      main: `${data.vendor_count} total`,
      sub: `${data.active_vendor_count} active`,
      icon: "🏢",
    },
    {
      label: "GL Codes",
      main: `${data.gl_code_count} total`,
      sub: `${data.active_gl_count} active`,
      icon: "📂",
    },
    {
      label: "Approval Rules",
      main: `${data.approval_rule_count} rules configured`,
      sub: data.has_capex_policy ? "CapEx policy set ✓" : "No CapEx policy",
      icon: "📋",
    },
    {
      label: "Historical Patterns",
      main: `${data.historical_pattern_count} records`,
      sub: data.historical_pattern_count >= 5 ? "Sufficient history ✓" : "Add more history",
      icon: "📈",
    },
  ];

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
        {metrics.map((m, i) => (
          <div key={i} style={{
            background: "#fff", borderRadius: 10,
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            padding: "20px 24px",
          }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>{m.icon}</div>
            <div style={{ fontSize: 13, color: "#64748b", marginBottom: 4 }}>{m.label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#1e293b" }}>{m.main}</div>
            <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>{m.sub}</div>
          </div>
        ))}
      </div>

      {data.warnings && data.warnings.length > 0 && (
        <div style={{
          background: "#fef9c3", border: "1px solid #fde047",
          borderRadius: 10, padding: "16px 20px",
        }}>
          <div style={{ fontWeight: 700, color: "#92400e", marginBottom: 10, fontSize: 14 }}>
            ⚠ Configuration Warnings
          </div>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {data.warnings.map((w, i) => (
              <li key={i} style={{ color: "#dc2626", fontSize: 13, marginBottom: 4 }}>{w}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CLIENT DASHBOARD (PortCo view)
// ---------------------------------------------------------------------------

function ClientDashboard({ authState, onLogout }) {
  const urlParams = new URLSearchParams(window.location.search);
  const initialTab = urlParams.get("tab") || "invoices";
  const highlightItem = urlParams.get("item") || "";
  const forcePortco = urlParams.get("portco") || "";

  const clientAxiosCfg = forcePortco
    ? { ...axiosCfg, params: { ...(axiosCfg.params || {}), portco: forcePortco } }
    : axiosCfg;

  const [invoices, setInvoices] = useState([]);
  const [triageItems, setTriageItems] = useState([]);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [toast, setToast] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const [portfolioData, setPortfolioData] = useState(null);
  const [auditData, setAuditData] = useState([]);
  const [truthBaseData, setTruthBaseData] = useState(null);

  const showToast = (type, message, duration = 4000) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), duration);
  };

  const fetchData = async () => {
    try {
      const [inv, tri, port, aud, tb] = await Promise.all([
        axios.get(`${API}/api/invoices`, clientAxiosCfg),
        axios.get(`${API}/api/triage`, clientAxiosCfg),
        axios.get(`${API}/api/portfolio`, clientAxiosCfg).catch(() => ({ data: null })),
        axios.get(`${API}/api/audit`, clientAxiosCfg).catch(() => ({ data: [] })),
        axios.get(`${API}/api/truth-base`, clientAxiosCfg).catch(() => ({ data: null })),
      ]);
      setInvoices(inv.data || []);
      setTriageItems(tri.data || []);
      setPortfolioData(port.data);
      setAuditData(aud.data || []);
      setTruthBaseData(tb.data);
    } catch { /* silent */ }
    setLoading(false);
    setRefreshing(false);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 120000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (highlightItem && !loading) {
      const el = document.getElementById(`triage-${highlightItem}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.style.boxShadow = "0 0 0 3px #1A9B8C";
        setTimeout(() => { el.style.boxShadow = ""; }, 3000);
      }
    }
  }, [loading, highlightItem]);

  const needsAttention = triageItems.filter(
    r => r.status === "Open" || r.status === "Escalated"
  );

  const handleAction = async (recordId, action) => {
    if (action === "reject") {
      if (!window.confirm("Are you sure you want to reject this invoice? This cannot be undone.")) {
        return;
      }
    }
    setActionLoading(recordId);
    try {
      const resp = await axios.post(
        `${API}/api/triage/${recordId}/resolve`,
        { action },
        clientAxiosCfg,
      );
      if (resp.data.ok) {
        const messages = {
          approve: "Invoice approved and sent to ERP",
          escalate: "Invoice escalated to Controller",
          reject: "Invoice rejected",
        };
        setToast({ type: "success", message: messages[action] || "Action completed" });
        setTimeout(() => setToast(null), 4000);
      }
      await fetchData();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Action failed";
      setToast({ type: "error", message: msg });
      setTimeout(() => setToast(null), 5000);
    }
    setActionLoading(null);
  };

  const statusPillStyle = (status) => {
    const map = {
      Processed: { bg: "#dcfce7", color: "#15803d" },
      Open:      { bg: "#fef9c3", color: "#92400e" },
      Escalated: { bg: "#ffedd5", color: "#c2410c" },
      Resolved:  { bg: "#dcfce7", color: "#15803d" },
      Rejected:  { bg: "#fee2e2", color: "#b91c1c" },
    };
    return map[status] || { bg: "#e2e8f0", color: "#475569" };
  };

  const fmtAmount = (v) => {
    if (v == null) return "—";
    try {
      return `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } catch { return "—"; }
  };

  const fmtDate = (d) => {
    if (!d) return "—";
    try {
      const dt = new Date(d);
      const mm = String(dt.getMonth() + 1).padStart(2, "0");
      const dd = String(dt.getDate()).padStart(2, "0");
      const yyyy = dt.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    } catch { return d; }
  };

  /* ── TriageDropdown ── */
  function TriageDropdown({ triageCode, recordId, item }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
      const handleClickOutside = (e) => {
        if (ref.current && !ref.current.contains(e.target)) setOpen(false);
      };
      if (open) document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open]);

    const ACTIONS = {
      UNKNOWN_VENDOR:            "Add Vendor to Truth Base",
      ERP_VENDOR_NOT_FOUND:      "Flag for ERP Setup",
      REQUIRES_SENIOR_APPROVAL:  "Escalate to\u2026",
      CAPEX_THRESHOLD_EXCEEDED:  "Confirm CapEx Coding",
      MATH_MISMATCH:             "View Line Items",
      GL_PATTERN_MISMATCH:       "Reassign GL Code",
      AMOUNT_ANOMALY:            "Confirm Amount",
    };

    const label = ACTIONS[triageCode];
    if (!label) return null;

    const MODAL_MAP = {
      UNKNOWN_VENDOR: "add_vendor",
      ERP_VENDOR_NOT_FOUND: "erp_vendor",
      REQUIRES_SENIOR_APPROVAL: "escalate_to",
      CAPEX_THRESHOLD_EXCEEDED: "capex",
      MATH_MISMATCH: "math_mismatch",
      GL_PATTERN_MISMATCH: "gl_reassign",
      AMOUNT_ANOMALY: "amount_anomaly",
    };

    const handleSelect = () => {
      setOpen(false);
      const modalType = MODAL_MAP[triageCode];
      if (modalType) {
        setActiveModal({ type: modalType, recordId, item });
      }
    };

    return (
      <div ref={ref} style={{ position: "absolute", top: 12, right: 12, zIndex: 10 }}>
        <button
          onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
          style={{
            width: 28, height: 28, borderRadius: "50%", border: "1px solid #e2e8f0",
            background: "#fff", cursor: "pointer", fontSize: 16, lineHeight: "28px",
            textAlign: "center", color: "#64748b", padding: 0,
          }}
        >⋯</button>
        {open && (
          <div style={{
            position: "absolute", top: 34, right: 0, minWidth: 200,
            background: "#fff", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
            border: "1px solid #e2e8f0", overflow: "hidden",
          }}>
            <div
              onClick={handleSelect}
              style={{
                padding: "10px 16px", fontSize: 13, fontWeight: 600, color: "#1e293b",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#f0fdfa"; e.currentTarget.style.color = "#1A9B8C"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#1e293b"; }}
            >
              {label}
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ── AddVendorModal ── */
  function AddVendorModal({ recordId, item, onClose }) {
    const [form, setForm] = useState({
      canonical_name: item.vendor_name || "",
      default_gl_code: "",
      expense_category: "",
      approval_level: "standard",
      notes: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
      setSubmitting(true);
      try {
        await axios.post(
          `${API}/api/triage/${recordId}/resolve`,
          { action: "add_rule", note: JSON.stringify(form) },
          clientAxiosCfg,
        );
        onClose();
        showToast("success", "Vendor added and triage item resolved");
        await fetchData();
      } catch (err) {
        showToast("error", err.response?.data?.error || "Failed to add vendor", 5000);
      }
      setSubmitting(false);
    };

    const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 4 };
    const inputStyle = {
      width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1",
      fontSize: 14, color: "#1e293b", boxSizing: "border-box",
    };

    return (
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex",
        alignItems: "center", justifyContent: "center", zIndex: 10000,
      }}>
        <div onClick={(e) => e.stopPropagation()} style={{
          background: "#fff", borderRadius: 12, maxWidth: 480, width: "90%",
          boxShadow: "0 8px 32px rgba(0,0,0,0.25)", overflow: "hidden",
        }}>
          <div style={{ background: "#0D1B2A", padding: "16px 24px" }}>
            <h3 style={{ margin: 0, color: "#fff", fontSize: 16, fontWeight: 700 }}>Add New Vendor</h3>
          </div>
          <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <label style={labelStyle}>Canonical Name</label>
              <input style={inputStyle} value={form.canonical_name}
                onChange={(e) => setForm({ ...form, canonical_name: e.target.value })} />
            </div>
            <div>
              <label style={labelStyle}>Default GL Code</label>
              <input style={inputStyle} value={form.default_gl_code}
                onChange={(e) => setForm({ ...form, default_gl_code: e.target.value })} />
            </div>
            <div>
              <label style={labelStyle}>Expense Category</label>
              <input style={inputStyle} value={form.expense_category}
                onChange={(e) => setForm({ ...form, expense_category: e.target.value })} />
            </div>
            <div>
              <label style={labelStyle}>Approval Level</label>
              <select style={inputStyle} value={form.approval_level}
                onChange={(e) => setForm({ ...form, approval_level: e.target.value })}>
                <option value="standard">Standard</option>
                <option value="senior">Senior</option>
                <option value="vp">VP</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Notes</label>
              <textarea style={{ ...inputStyle, minHeight: 60, resize: "vertical" }} value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
              <button onClick={onClose} style={{
                padding: "8px 20px", borderRadius: 6, border: "1px solid #cbd5e1",
                background: "#fff", color: "#475569", fontWeight: 600, fontSize: 13, cursor: "pointer",
              }}>Cancel</button>
              <button onClick={handleSubmit} disabled={submitting} style={{
                padding: "8px 20px", borderRadius: 6, border: "none",
                background: "#1A9B8C", color: "#fff", fontWeight: 600, fontSize: 13,
                cursor: submitting ? "default" : "pointer", opacity: submitting ? 0.6 : 1,
              }}>{submitting ? "Saving\u2026" : "Add Vendor & Resolve"}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── EscalateModal ── */
  function EscalateModal({ recordId, item, onClose }) {
    const [target, setTarget] = useState("controller");
    const [otherEmail, setOtherEmail] = useState("");
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
      setSubmitting(true);
      const escalateTo = target === "other" ? otherEmail : target;
      try {
        await axios.post(
          `${API}/api/triage/${recordId}/resolve`,
          { action: "escalate", note: `Escalated to: ${escalateTo}${note ? ". " + note : ""}` },
          clientAxiosCfg,
        );
        onClose();
        showToast("success", `Invoice escalated to ${escalateTo}`);
        await fetchData();
      } catch (err) {
        showToast("error", err.response?.data?.error || "Failed to escalate", 5000);
      }
      setSubmitting(false);
    };

    const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 4 };
    const inputStyle = {
      width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1",
      fontSize: 14, color: "#1e293b", boxSizing: "border-box",
    };
    const radioRow = (value, label) => (
      <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14, color: "#1e293b" }}>
        <input type="radio" name="escalate_target" value={value}
          checked={target === value} onChange={() => setTarget(value)} />
        {label}
      </label>
    );

    return (
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex",
        alignItems: "center", justifyContent: "center", zIndex: 10000,
      }}>
        <div onClick={(e) => e.stopPropagation()} style={{
          background: "#fff", borderRadius: 12, maxWidth: 480, width: "90%",
          boxShadow: "0 8px 32px rgba(0,0,0,0.25)", overflow: "hidden",
        }}>
          <div style={{ background: "#0D1B2A", padding: "16px 24px" }}>
            <h3 style={{ margin: 0, color: "#fff", fontSize: 16, fontWeight: 700 }}>Escalate Invoice</h3>
          </div>
          <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{
              background: "#f8fafc", borderRadius: 6, padding: "10px 14px",
              fontSize: 13, color: "#475569", lineHeight: 1.6,
            }}>
              <strong>{item.vendor_name || "Unknown"}</strong> &mdash; Invoice #{item.invoice_number || "\u2014"}<br />
              Amount: <strong>{fmtAmount(item.total_amount)}</strong>
            </div>
            <div>
              <label style={labelStyle}>Escalate to</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
                {radioRow("controller", "Controller")}
                {radioRow("vp_finance", "VP Finance")}
                {radioRow("other", "Other")}
              </div>
            </div>
            {target === "other" && (
              <div>
                <label style={labelStyle}>Email address</label>
                <input style={inputStyle} type="email" placeholder="name@company.com"
                  value={otherEmail} onChange={(e) => setOtherEmail(e.target.value)} />
              </div>
            )}
            <div>
              <label style={labelStyle}>Note (optional)</label>
              <textarea style={{ ...inputStyle, minHeight: 60, resize: "vertical" }}
                value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
              <button onClick={onClose} style={{
                padding: "8px 20px", borderRadius: 6, border: "1px solid #cbd5e1",
                background: "#fff", color: "#475569", fontWeight: 600, fontSize: 13, cursor: "pointer",
              }}>Cancel</button>
              <button onClick={handleSubmit} disabled={submitting} style={{
                padding: "8px 20px", borderRadius: 6, border: "none",
                background: "#f97316", color: "#fff", fontWeight: 600, fontSize: 13,
                cursor: submitting ? "default" : "pointer", opacity: submitting ? 0.6 : 1,
              }}>{submitting ? "Escalating\u2026" : "Escalate"}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── CapExModal ── */
  function CapExModal({ recordId, item, onClose }) {
    const [decision, setDecision] = useState("opex");
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
      setSubmitting(true);
      const label = decision === "opex" ? "Operating Expense (OpEx)" : "Capital Expenditure (CapEx)";
      try {
        await axios.post(
          `${API}/api/triage/${recordId}/resolve`,
          { action: "approve", note: `CapEx coding confirmed: ${label}${note ? ". " + note : ""}` },
          clientAxiosCfg,
        );
        onClose();
        showToast("success", `Confirmed as ${label}`);
        await fetchData();
      } catch (err) {
        showToast("error", err.response?.data?.error || "Failed to resolve", 5000);
      }
      setSubmitting(false);
    };

    const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 4 };
    const inputStyle = {
      width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1",
      fontSize: 14, color: "#1e293b", boxSizing: "border-box",
    };

    return (
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex",
        alignItems: "center", justifyContent: "center", zIndex: 10000,
      }}>
        <div onClick={(e) => e.stopPropagation()} style={{
          background: "#fff", borderRadius: 12, maxWidth: 480, width: "90%",
          boxShadow: "0 8px 32px rgba(0,0,0,0.25)", overflow: "hidden",
        }}>
          <div style={{ background: "#0D1B2A", padding: "16px 24px" }}>
            <h3 style={{ margin: 0, color: "#fff", fontSize: 16, fontWeight: 700 }}>Confirm CapEx Coding</h3>
          </div>
          <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{
              background: "#f8fafc", borderRadius: 6, padding: "10px 14px",
              fontSize: 13, color: "#475569", lineHeight: 1.6,
            }}>
              <strong>{item.vendor_name || "Unknown"}</strong> &mdash; Invoice #{item.invoice_number || "\u2014"}<br />
              Amount: <strong>{fmtAmount(item.total_amount)}</strong>
            </div>
            <div style={{
              background: "#fef9c3", borderRadius: 6, padding: "10px 14px",
              fontSize: 13, color: "#92400e", lineHeight: 1.5,
            }}>
              {item.triage_message_full || item.triage_message || "No details available."}
            </div>
            <div>
              <label style={labelStyle}>Classification</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14, color: "#1e293b" }}>
                  <input type="radio" name="capex_decision" checked={decision === "opex"} onChange={() => setDecision("opex")} />
                  Confirm as Operating Expense (OpEx)
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14, color: "#1e293b" }}>
                  <input type="radio" name="capex_decision" checked={decision === "capex"} onChange={() => setDecision("capex")} />
                  Recode as Capital Expenditure (CapEx)
                </label>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Note (optional)</label>
              <textarea style={{ ...inputStyle, minHeight: 60, resize: "vertical" }}
                value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
              <button onClick={onClose} style={{
                padding: "8px 20px", borderRadius: 6, border: "1px solid #cbd5e1",
                background: "#fff", color: "#475569", fontWeight: 600, fontSize: 13, cursor: "pointer",
              }}>Cancel</button>
              <button onClick={handleSubmit} disabled={submitting} style={{
                padding: "8px 20px", borderRadius: 6, border: "none",
                background: "#1A9B8C", color: "#fff", fontWeight: 600, fontSize: 13,
                cursor: submitting ? "default" : "pointer", opacity: submitting ? 0.6 : 1,
              }}>{submitting ? "Saving\u2026" : "Confirm & Resolve"}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── MathMismatchModal ── */
  function MathMismatchModal({ recordId, item, onClose }) {
    const [choice, setChoice] = useState("accept_stated");
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
      setSubmitting(true);
      const labels = {
        accept_stated: "Accept stated total (vendor is correct)",
        accept_calculated: "Accept calculated total (our math is correct)",
        flag_review: "Flagged for manual review",
      };
      const action = choice === "flag_review" ? "escalate" : "approve";
      try {
        await axios.post(
          `${API}/api/triage/${recordId}/resolve`,
          { action, note: `Math mismatch resolved: ${labels[choice]}${note ? ". " + note : ""}` },
          clientAxiosCfg,
        );
        onClose();
        showToast("success", choice === "flag_review" ? "Escalated for manual review" : "Math mismatch resolved");
        await fetchData();
      } catch (err) {
        showToast("error", err.response?.data?.error || "Failed to resolve", 5000);
      }
      setSubmitting(false);
    };

    const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 4 };
    const inputStyle = {
      width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1",
      fontSize: 14, color: "#1e293b", boxSizing: "border-box",
    };

    return (
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex",
        alignItems: "center", justifyContent: "center", zIndex: 10000,
      }}>
        <div onClick={(e) => e.stopPropagation()} style={{
          background: "#fff", borderRadius: 12, maxWidth: 480, width: "90%",
          boxShadow: "0 8px 32px rgba(0,0,0,0.25)", overflow: "hidden",
        }}>
          <div style={{ background: "#0D1B2A", padding: "16px 24px" }}>
            <h3 style={{ margin: 0, color: "#fff", fontSize: 16, fontWeight: 700 }}>Math Mismatch Review</h3>
          </div>
          <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{
              background: "#f8fafc", borderRadius: 6, padding: "10px 14px",
              fontSize: 13, color: "#475569", lineHeight: 1.6,
            }}>
              <strong>{item.vendor_name || "Unknown"}</strong> &mdash; Invoice #{item.invoice_number || "\u2014"}<br />
              Amount: <strong>{fmtAmount(item.total_amount)}</strong>
            </div>
            <div style={{
              background: "#ffedd5", borderRadius: 6, padding: "10px 14px",
              fontSize: 13, color: "#c2410c", lineHeight: 1.5,
            }}>
              {item.triage_message_full || item.triage_message || "No details available."}
            </div>
            <div>
              <label style={labelStyle}>Resolution</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14, color: "#1e293b" }}>
                  <input type="radio" name="math_choice" checked={choice === "accept_stated"} onChange={() => setChoice("accept_stated")} />
                  Accept stated total (vendor is correct)
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14, color: "#1e293b" }}>
                  <input type="radio" name="math_choice" checked={choice === "accept_calculated"} onChange={() => setChoice("accept_calculated")} />
                  Accept calculated total (our math is correct)
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14, color: "#1e293b" }}>
                  <input type="radio" name="math_choice" checked={choice === "flag_review"} onChange={() => setChoice("flag_review")} />
                  Flag for manual review
                </label>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Note (optional)</label>
              <textarea style={{ ...inputStyle, minHeight: 60, resize: "vertical" }}
                value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
              <button onClick={onClose} style={{
                padding: "8px 20px", borderRadius: 6, border: "1px solid #cbd5e1",
                background: "#fff", color: "#475569", fontWeight: 600, fontSize: 13, cursor: "pointer",
              }}>Cancel</button>
              <button onClick={handleSubmit} disabled={submitting} style={{
                padding: "8px 20px", borderRadius: 6, border: "none",
                background: "#1A9B8C", color: "#fff", fontWeight: 600, fontSize: 13,
                cursor: submitting ? "default" : "pointer", opacity: submitting ? 0.6 : 1,
              }}>{submitting ? "Resolving\u2026" : "Resolve"}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── GLReassignModal ── */
  function GLReassignModal({ recordId, item, onClose }) {
    const extractGL = () => {
      const msg = item.triage_message_full || item.triage_message || "";
      const match = msg.match(/assigned[:\s]+(\d{4})/i) || msg.match(/GL[:\s]+(\d{4})/i);
      return match ? match[1] : "";
    };
    const [glCode, setGlCode] = useState(extractGL());
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
      setSubmitting(true);
      try {
        await axios.post(
          `${API}/api/triage/${recordId}/resolve`,
          { action: "approve", note: `GL code reassigned to: ${glCode}${note ? ". " + note : ""}` },
          clientAxiosCfg,
        );
        onClose();
        showToast("success", `GL code reassigned to ${glCode}`);
        await fetchData();
      } catch (err) {
        showToast("error", err.response?.data?.error || "Failed to resolve", 5000);
      }
      setSubmitting(false);
    };

    const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 4 };
    const inputStyle = {
      width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1",
      fontSize: 14, color: "#1e293b", boxSizing: "border-box",
    };

    return (
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex",
        alignItems: "center", justifyContent: "center", zIndex: 10000,
      }}>
        <div onClick={(e) => e.stopPropagation()} style={{
          background: "#fff", borderRadius: 12, maxWidth: 480, width: "90%",
          boxShadow: "0 8px 32px rgba(0,0,0,0.25)", overflow: "hidden",
        }}>
          <div style={{ background: "#0D1B2A", padding: "16px 24px" }}>
            <h3 style={{ margin: 0, color: "#fff", fontSize: 16, fontWeight: 700 }}>Reassign GL Code</h3>
          </div>
          <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{
              background: "#f8fafc", borderRadius: 6, padding: "10px 14px",
              fontSize: 13, color: "#475569", lineHeight: 1.6,
            }}>
              <strong>{item.vendor_name || "Unknown"}</strong> &mdash; Invoice #{item.invoice_number || "\u2014"}<br />
              Amount: <strong>{fmtAmount(item.total_amount)}</strong>
            </div>
            <div style={{
              background: "#e0e7ff", borderRadius: 6, padding: "10px 14px",
              fontSize: 13, color: "#3730a3", lineHeight: 1.5,
            }}>
              {item.triage_message_full || item.triage_message || "No details available."}
            </div>
            <div>
              <label style={labelStyle}>New GL Code</label>
              <input style={inputStyle} value={glCode} placeholder="e.g. 6210"
                onChange={(e) => setGlCode(e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Note (optional)</label>
              <textarea style={{ ...inputStyle, minHeight: 60, resize: "vertical" }}
                value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
              <button onClick={onClose} style={{
                padding: "8px 20px", borderRadius: 6, border: "1px solid #cbd5e1",
                background: "#fff", color: "#475569", fontWeight: 600, fontSize: 13, cursor: "pointer",
              }}>Cancel</button>
              <button onClick={handleSubmit} disabled={submitting} style={{
                padding: "8px 20px", borderRadius: 6, border: "none",
                background: "#1A9B8C", color: "#fff", fontWeight: 600, fontSize: 13,
                cursor: submitting ? "default" : "pointer", opacity: submitting ? 0.6 : 1,
              }}>{submitting ? "Saving\u2026" : "Reassign & Resolve"}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── AmountAnomalyModal ── */
  function AmountAnomalyModal({ recordId, item, onClose }) {
    const [choice, setChoice] = useState("confirm");
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
      setSubmitting(true);
      const action = choice === "confirm" ? "approve" : "escalate";
      const label = choice === "confirm" ? "Amount confirmed as correct" : "Flagged as suspicious — escalated";
      try {
        await axios.post(
          `${API}/api/triage/${recordId}/resolve`,
          { action, note: `${label}${note ? ". " + note : ""}` },
          clientAxiosCfg,
        );
        onClose();
        showToast("success", choice === "confirm" ? "Amount confirmed" : "Escalated for review");
        await fetchData();
      } catch (err) {
        showToast("error", err.response?.data?.error || "Failed to resolve", 5000);
      }
      setSubmitting(false);
    };

    const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 4 };
    const inputStyle = {
      width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1",
      fontSize: 14, color: "#1e293b", boxSizing: "border-box",
    };

    return (
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex",
        alignItems: "center", justifyContent: "center", zIndex: 10000,
      }}>
        <div onClick={(e) => e.stopPropagation()} style={{
          background: "#fff", borderRadius: 12, maxWidth: 480, width: "90%",
          boxShadow: "0 8px 32px rgba(0,0,0,0.25)", overflow: "hidden",
        }}>
          <div style={{ background: "#0D1B2A", padding: "16px 24px" }}>
            <h3 style={{ margin: 0, color: "#fff", fontSize: 16, fontWeight: 700 }}>Amount Anomaly Review</h3>
          </div>
          <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{
              background: "#f8fafc", borderRadius: 6, padding: "10px 14px",
              fontSize: 13, color: "#475569", lineHeight: 1.6,
            }}>
              <strong>{item.vendor_name || "Unknown"}</strong> &mdash; Invoice #{item.invoice_number || "\u2014"}<br />
              Amount: <strong>{fmtAmount(item.total_amount)}</strong>
            </div>
            <div style={{
              background: "#fef9c3", borderRadius: 6, padding: "10px 14px",
              fontSize: 13, color: "#92400e", lineHeight: 1.5,
            }}>
              {item.triage_message_full || item.triage_message || "No details available."}
            </div>
            <div>
              <label style={labelStyle}>Decision</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14, color: "#1e293b" }}>
                  <input type="radio" name="anomaly_choice" checked={choice === "confirm"} onChange={() => setChoice("confirm")} />
                  Confirm amount is correct
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14, color: "#1e293b" }}>
                  <input type="radio" name="anomaly_choice" checked={choice === "flag"} onChange={() => setChoice("flag")} />
                  Flag as suspicious — escalate
                </label>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Note (optional)</label>
              <textarea style={{ ...inputStyle, minHeight: 60, resize: "vertical" }}
                value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
              <button onClick={onClose} style={{
                padding: "8px 20px", borderRadius: 6, border: "1px solid #cbd5e1",
                background: "#fff", color: "#475569", fontWeight: 600, fontSize: 13, cursor: "pointer",
              }}>Cancel</button>
              <button onClick={handleSubmit} disabled={submitting} style={{
                padding: "8px 20px", borderRadius: 6, border: "none",
                background: "#1A9B8C", color: "#fff", fontWeight: 600, fontSize: 13,
                cursor: submitting ? "default" : "pointer", opacity: submitting ? 0.6 : 1,
              }}>{submitting ? "Resolving\u2026" : "Resolve"}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── ERPVendorModal ── */
  function ERPVendorModal({ recordId, item, onClose }) {
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
      setSubmitting(true);
      try {
        await axios.post(
          `${API}/api/triage/${recordId}/resolve`,
          { action: "approve", note: `Vendor created in ERP${note ? ". " + note : ""}` },
          clientAxiosCfg,
        );
        onClose();
        showToast("success", "Marked as resolved — vendor created in ERP");
        await fetchData();
      } catch (err) {
        showToast("error", err.response?.data?.error || "Failed to resolve", 5000);
      }
      setSubmitting(false);
    };

    const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 4 };
    const inputStyle = {
      width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1",
      fontSize: 14, color: "#1e293b", boxSizing: "border-box",
    };

    return (
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex",
        alignItems: "center", justifyContent: "center", zIndex: 10000,
      }}>
        <div onClick={(e) => e.stopPropagation()} style={{
          background: "#fff", borderRadius: 12, maxWidth: 480, width: "90%",
          boxShadow: "0 8px 32px rgba(0,0,0,0.25)", overflow: "hidden",
        }}>
          <div style={{ background: "#0D1B2A", padding: "16px 24px" }}>
            <h3 style={{ margin: 0, color: "#fff", fontSize: 16, fontWeight: 700 }}>Vendor Missing from ERP</h3>
          </div>
          <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{
              background: "#f8fafc", borderRadius: 6, padding: "10px 14px",
              fontSize: 13, color: "#475569", lineHeight: 1.6,
            }}>
              <strong>{item.vendor_name || "Unknown"}</strong> &mdash; Invoice #{item.invoice_number || "\u2014"}<br />
              Amount: <strong>{fmtAmount(item.total_amount)}</strong>
            </div>
            <div style={{
              background: "#fee2e2", borderRadius: 6, padding: "10px 14px",
              fontSize: 13, color: "#b91c1c", lineHeight: 1.5,
            }}>
              {item.triage_message_full || item.triage_message || "No details available."}
            </div>
            <div style={{
              background: "#eff6ff", borderRadius: 6, padding: "12px 14px",
              fontSize: 13, color: "#1e40af", lineHeight: 1.6,
            }}>
              This vendor exists in the Flonance Truth Base but has not been set up in your ERP (QuickBooks/NetSuite).
              Please create the vendor in your ERP, then resolve this item.
            </div>
            <div>
              <label style={labelStyle}>Note (optional)</label>
              <textarea style={{ ...inputStyle, minHeight: 60, resize: "vertical" }}
                value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
              <button onClick={onClose} style={{
                padding: "8px 20px", borderRadius: 6, border: "1px solid #cbd5e1",
                background: "#fff", color: "#475569", fontWeight: 600, fontSize: 13, cursor: "pointer",
              }}>Cancel</button>
              <button onClick={handleSubmit} disabled={submitting} style={{
                padding: "8px 20px", borderRadius: 6, border: "none",
                background: "#1A9B8C", color: "#fff", fontWeight: 600, fontSize: 13,
                cursor: submitting ? "default" : "pointer", opacity: submitting ? 0.6 : 1,
              }}>{submitting ? "Saving\u2026" : "Mark as Resolved"}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const portcoName = authState.portco_name || "Dashboard";

  return (
    <div style={{
      minHeight: "100vh",
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
      background: "#0D1B2A",
    }}>
      {toast && (
        <div style={{
          position: "fixed",
          bottom: 24,
          right: 24,
          padding: "14px 24px",
          borderRadius: 8,
          color: "#fff",
          fontWeight: 600,
          fontSize: 14,
          zIndex: 9999,
          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          background: toast.type === "success" ? "#065F46" : "#991B1B",
          animation: "fadeIn 0.2s ease",
        }}>
          {toast.message}
        </div>
      )}
      {activeModal?.type === "add_vendor" && (
        <AddVendorModal recordId={activeModal.recordId} item={activeModal.item}
          onClose={() => setActiveModal(null)} />
      )}
      {activeModal?.type === "escalate_to" && (
        <EscalateModal recordId={activeModal.recordId} item={activeModal.item}
          onClose={() => setActiveModal(null)} />
      )}
      {activeModal?.type === "capex" && (
        <CapExModal recordId={activeModal.recordId} item={activeModal.item}
          onClose={() => setActiveModal(null)} />
      )}
      {activeModal?.type === "math_mismatch" && (
        <MathMismatchModal recordId={activeModal.recordId} item={activeModal.item}
          onClose={() => setActiveModal(null)} />
      )}
      {activeModal?.type === "gl_reassign" && (
        <GLReassignModal recordId={activeModal.recordId} item={activeModal.item}
          onClose={() => setActiveModal(null)} />
      )}
      {activeModal?.type === "amount_anomaly" && (
        <AmountAnomalyModal recordId={activeModal.recordId} item={activeModal.item}
          onClose={() => setActiveModal(null)} />
      )}
      {activeModal?.type === "erp_vendor" && (
        <ERPVendorModal recordId={activeModal.recordId} item={activeModal.item}
          onClose={() => setActiveModal(null)} />
      )}
      {/* Header */}
      <div style={{
        background: "#0D1B2A",
        borderBottom: "1px solid #1e3a5f",
        padding: "0 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: 56,
      }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
          <span style={{ color: "#1A9B8C", fontWeight: 800, fontSize: 18, letterSpacing: 1 }}>FLONANCE</span>
          <span style={{ color: "#fff", fontSize: 14, fontWeight: 500 }}>{portcoName}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            style={{
              background: "none", border: "1px solid #475569",
              color: "#94a3b8", borderRadius: 6, padding: "5px 14px",
              fontSize: 12, fontWeight: 600, cursor: refreshing ? "default" : "pointer",
              opacity: refreshing ? 0.6 : 1,
              transition: "opacity 0.2s",
            }}
          >
            {refreshing ? "Refreshing\u2026" : "\u21BB Refresh"}
          </button>
          <button
            onClick={onLogout}
            style={{
              background: "none", border: "1px solid #475569",
              color: "#94a3b8", borderRadius: 6, padding: "5px 14px",
              fontSize: 12, fontWeight: 600, cursor: "pointer",
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        background: "#0D1B2A",
        borderBottom: "1px solid #1e3a5f",
        display: "flex",
        padding: "0 24px",
        gap: 4,
      }}>
        <button
          onClick={() => setActiveTab("invoices")}
          style={{
            background: "none", border: "none", cursor: "pointer",
            padding: "14px 20px", fontSize: 14, fontWeight: 600,
            color: activeTab === "invoices" ? "#1A9B8C" : "#94a3b8",
            borderBottom: activeTab === "invoices" ? "2px solid #1A9B8C" : "2px solid transparent",
            marginBottom: -1,
          }}
        >
          All Invoices
        </button>
        <button
          onClick={() => setActiveTab("attention")}
          style={{
            background: "none", border: "none", cursor: "pointer",
            padding: "14px 20px", fontSize: 14, fontWeight: 600,
            color: activeTab === "attention" ? "#1A9B8C" : "#94a3b8",
            borderBottom: activeTab === "attention" ? "2px solid #1A9B8C" : "2px solid transparent",
            marginBottom: -1,
          }}
        >
          Needs Attention
          {needsAttention.length > 0 && (
            <span style={{
              background: "#dc2626", color: "#fff",
              borderRadius: 10, padding: "1px 7px",
              fontSize: 11, fontWeight: 700, marginLeft: 8,
            }}>
              {needsAttention.length}
            </span>
          )}
        </button>
        {["portfolio", "audit", "truth-base"].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              padding: "14px 20px", fontSize: 14, fontWeight: 600,
              color: activeTab === tab ? "#1A9B8C" : "#94a3b8",
              borderBottom: activeTab === tab ? "2px solid #1A9B8C" : "2px solid transparent",
              marginBottom: -1,
            }}
          >
            {tab === "portfolio" ? "Portfolio Health" : tab === "audit" ? "Audit Log" : "Truth Base"}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
        {loading && (
          <p style={{ color: "#94a3b8", textAlign: "center", marginTop: 40 }}>Loading...</p>
        )}
        {!loading && activeTab === "invoices" && (
          <div style={{
            background: "#fff", borderRadius: 10,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)", overflow: "hidden",
          }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc", color: "#64748b", textAlign: "left" }}>
                  {["Date", "Vendor", "Invoice #", "Amount", "Status"].map(h => (
                    <th key={h} style={{
                      padding: "12px 16px", fontWeight: 600,
                      borderBottom: "1px solid #e2e8f0",
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ padding: 32, textAlign: "center", color: "#94a3b8" }}>
                      No invoices found.
                    </td>
                  </tr>
                ) : invoices.map((inv, i) => {
                  const pill = statusPillStyle(inv.status);
                  return (
                    <tr key={`${inv.job_id}-${i}`} style={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
                      <td style={{ padding: "10px 16px", borderBottom: "1px solid #e2e8f0", color: "#475569" }}>
                        {fmtDate(inv.date)}
                      </td>
                      <td style={{ padding: "10px 16px", borderBottom: "1px solid #e2e8f0", fontWeight: 500 }}>
                        {inv.vendor_name || "—"}
                      </td>
                      <td style={{ padding: "10px 16px", borderBottom: "1px solid #e2e8f0" }}>
                        {inv.invoice_number || "—"}
                      </td>
                      <td style={{ padding: "10px 16px", borderBottom: "1px solid #e2e8f0", fontWeight: 600 }}>
                        {fmtAmount(inv.total_amount)}
                      </td>
                      <td style={{ padding: "10px 16px", borderBottom: "1px solid #e2e8f0" }}>
                        <span style={{
                          background: pill.bg, color: pill.color,
                          borderRadius: 12, padding: "3px 10px",
                          fontSize: 11, fontWeight: 600,
                        }}>
                          {inv.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {!loading && activeTab === "attention" && (
          <div>
            {needsAttention.length === 0 ? (
              <p style={{ color: "#16a34a", fontWeight: 600, textAlign: "center", marginTop: 40 }}>
                No items need attention. All clear.
              </p>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                {needsAttention.map(item => {
                  const isLoading = actionLoading === item.record_id;
                  const codePillColors = {
                    UNKNOWN_VENDOR: { bg: "#fee2e2", color: "#b91c1c" },
                    MATH_MISMATCH:  { bg: "#ffedd5", color: "#c2410c" },
                    AMOUNT_ANOMALY: { bg: "#fef9c3", color: "#92400e" },
                  };
                  const cPill = codePillColors[item.triage_code] || { bg: "#e0e7ff", color: "#3730a3" };

                  return (
                    <div key={item.record_id} id={`triage-${item.record_id}`} style={{
                      background: "#fff", borderRadius: 10,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      padding: 20, display: "flex", flexDirection: "column",
                      opacity: isLoading ? 0.6 : 1,
                      transition: "opacity 0.2s",
                      position: "relative",
                    }}>
                      <TriageDropdown triageCode={item.triage_code} recordId={item.record_id} item={item} />
                      {/* Top */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 15, color: "#1e293b", marginBottom: 2 }}>
                            {item.vendor_name || "Unknown Vendor"}
                          </div>
                          <div style={{ fontSize: 13, color: "#64748b" }}>
                            Invoice #{item.invoice_number || "—"}
                          </div>
                        </div>
                        <span style={{
                          background: cPill.bg, color: cPill.color,
                          borderRadius: 12, padding: "3px 10px",
                          fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
                        }}>
                          {item.triage_code}
                        </span>
                      </div>

                      {/* Amount + hours */}
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                        <span style={{ fontSize: 20, fontWeight: 700, color: "#1e293b" }}>
                          {fmtAmount(item.total_amount)}
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: hours_color(item.hours_open) }}>
                          {item.hours_open}h open
                        </span>
                      </div>

                      {/* Message */}
                      <div style={{
                        background: "#f8fafc", borderRadius: 6, padding: "10px 12px",
                        fontSize: 13, color: "#475569", lineHeight: 1.5,
                        marginBottom: 16, flex: 1,
                      }}>
                        {item.triage_message_full || item.triage_message || "No details available."}
                      </div>

                      {/* Actions */}
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          disabled={isLoading}
                          onClick={() => handleAction(item.record_id, "approve")}
                          style={{
                            flex: 1, padding: "8px 0", borderRadius: 6,
                            background: "#1A9B8C", color: "#fff",
                            border: "none", fontWeight: 600, fontSize: 13,
                            cursor: isLoading ? "default" : "pointer",
                          }}
                        >
                          {isLoading ? "..." : "Approve"}
                        </button>
                        <button
                          disabled={isLoading}
                          onClick={() => handleAction(item.record_id, "escalate")}
                          style={{
                            flex: 1, padding: "8px 0", borderRadius: 6,
                            background: "#f97316", color: "#fff",
                            border: "none", fontWeight: 600, fontSize: 13,
                            cursor: isLoading ? "default" : "pointer",
                          }}
                        >
                          {isLoading ? "..." : "Escalate"}
                        </button>
                        <button
                          disabled={isLoading}
                          onClick={() => handleAction(item.record_id, "reject")}
                          style={{
                            flex: 1, padding: "8px 0", borderRadius: 6,
                            background: "#fff", color: "#dc2626",
                            border: "2px solid #dc2626", fontWeight: 600, fontSize: 13,
                            cursor: isLoading ? "default" : "pointer",
                          }}
                        >
                          {isLoading ? "..." : "Reject"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {!loading && activeTab === "portfolio" && (
          portfolioData ? <PortfolioView data={portfolioData} loading={false} /> : <p style={{color: "#94a3b8", textAlign: "center", marginTop: 40}}>No portfolio data available yet.</p>
        )}
        {!loading && activeTab === "audit" && (
          <AuditView data={auditData} />
        )}
        {!loading && activeTab === "truth-base" && (
          truthBaseData ? <TruthBaseView data={truthBaseData} /> : <p style={{color: "#94a3b8", textAlign: "center", marginTop: 40}}>No Truth Base data available yet.</p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ROOT APP
// ---------------------------------------------------------------------------

export default function App() {
  const [authState,     setAuthState]     = useState(null);
  const [portfolioData, setPortfolioData] = useState(null);
  const [triageData,    setTriageData]    = useState([]);
  const [auditData,     setAuditData]     = useState([]);
  const [truthBaseData, setTruthBaseData] = useState(null);
  const [alertData,     setAlertData]     = useState(null);
  const [activeView,    setActiveView]    = useState("portfolio");
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [lastRefresh,   setLastRefresh]   = useState(null);

  // Check auth on mount
  useEffect(() => {
    axios.get(`${API}/auth/status`, axiosCfg)
      .then(res => {
        if (res.data.authenticated) {
          setAuthState({ role: res.data.role, portco_id: res.data.portco_id });
        } else {
          setAuthState("unauthenticated");
        }
      })
      .catch(() => setAuthState("unauthenticated"));
  }, []);

  const fetchAll = async () => {
    try {
      const [port, triage, audit, truth, alerts] = await Promise.all([
        axios.get(`${API}/api/portfolio`, axiosCfg),
        axios.get(`${API}/api/triage`, axiosCfg),
        axios.get(`${API}/api/audit`, axiosCfg),
        axios.get(`${API}/api/truth-base`, axiosCfg),
        axios.get(`${API}/api/alerts`, axiosCfg),
      ]);
      setPortfolioData(port.data);
      setTriageData(triage.data);
      setAuditData(audit.data);
      setTruthBaseData(truth.data);
      setAlertData(alerts.data);
      setLastRefresh(new Date());
      setError(null);
    } catch (err) {
      setError("Failed to load data from API. Is the Flask server running?");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authState || authState === "unauthenticated") return;
    fetchAll();
    const interval = setInterval(fetchAll, 120000);
    return () => clearInterval(interval);
  }, [authState]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/logout`, {}, axiosCfg);
    } catch { /* ignore */ }
    setAuthState("unauthenticated");
  };

  const handleLogin = (data) => {
    setAuthState({ role: data.role, portco_name: data.portco_name });
  };

  const urlParams = new URLSearchParams(window.location.search);
  const forceClientView = urlParams.get("view") === "client";

  // --- Conditional rendering ---

  if (authState === null) {
    return (
      <div style={{
        minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "'Inter', 'Segoe UI', sans-serif", background: "#0D1B2A", color: "#94a3b8",
      }}>
        Loading...
      </div>
    );
  }

  if (authState === "unauthenticated") {
    return <LoginPage onLogin={handleLogin} />;
  }

  if (authState.role === "client" || (authState.role === "admin" && forceClientView)) {
    return <ClientDashboard authState={authState} onLogout={handleLogout} />;
  }

  const headerTitle = "Oversight Dashboard";

  const tabs = [
    { id: "portfolio",   label: "Portfolio Health" },
    { id: "triage",      label: "Triage Queue" },
    { id: "audit",       label: "Audit Log" },
    { id: "truth-base",  label: "Truth Base" },
  ];

  const alertCount = alertData ? alertData.total_alert_count : 0;

  return (
    <div style={{ minHeight: "100vh", fontFamily: "'Inter', 'Segoe UI', sans-serif", background: "#f1f5f9" }}>

      {/* Header */}
      <div style={{
        background: "#0f172a", padding: "0 24px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        height: 56,
      }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
          <span style={{ color: "#fff", fontWeight: 800, fontSize: 18, letterSpacing: 1 }}>FLONANCE</span>
          <span style={{ color: "#14b8a6", fontSize: 13 }}>{headerTitle}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {lastRefresh && (
            <span style={{ color: "#94a3b8", fontSize: 12 }}>
              Last updated: {fmt_time(lastRefresh)}
            </span>
          )}
          {alertCount > 0 && (
            <span style={{
              background: "#dc2626", color: "#fff",
              borderRadius: 12, padding: "2px 10px",
              fontSize: 12, fontWeight: 700,
            }}>
              {alertCount} alert{alertCount !== 1 ? "s" : ""}
            </span>
          )}
          <button
            onClick={handleLogout}
            style={{
              background: "none", border: "1px solid #475569",
              color: "#94a3b8", borderRadius: 6, padding: "5px 14px",
              fontSize: 12, fontWeight: 600, cursor: "pointer",
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{
        background: "#fff", borderBottom: "1px solid #e2e8f0",
        display: "flex", padding: "0 24px",
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveView(tab.id)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              padding: "14px 20px", fontSize: 14, fontWeight: 600,
              color: activeView === tab.id ? "#0d9488" : "#64748b",
              borderBottom: activeView === tab.id ? "2px solid #0d9488" : "2px solid transparent",
              marginBottom: -1,
              transition: "color 0.15s",
            }}
          >
            {tab.label}
            {tab.id === "triage" && triageData.length > 0 && (
              <span style={{
                background: "#dc2626", color: "#fff",
                borderRadius: 10, padding: "1px 7px",
                fontSize: 11, fontWeight: 700, marginLeft: 6,
              }}>
                {triageData.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
        {error && (
          <div style={{
            background: "#fee2e2", border: "1px solid #fca5a5",
            borderRadius: 8, padding: "12px 16px", marginBottom: 16,
            color: "#991b1b", fontSize: 13,
          }}>
            {error}
          </div>
        )}

        {activeView === "portfolio"  && <PortfolioView data={portfolioData} loading={loading} />}
        {activeView === "triage"     && <TriageView    data={triageData} />}
        {activeView === "audit"      && <AuditView     data={auditData} />}
        {activeView === "truth-base" && <TruthBaseView data={truthBaseData} />}
      </div>
    </div>
  );
}
